import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../images/logo/logo.png";
import { 
  Navbar, Nav, Container, 
  // Dropdown 
} from "react-bootstrap";
import { FaUser, FaSignOutAlt, FaUserCircle, FaUserPlus } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import Login from "./auth/Login";
import SignUpModal from "./auth/SignUp";

const navItems = [
  { name: "Home", link: "/home" },
  { name: "About Us", link: "/aboutus" },
  { name: "Auction", link: "/auction" },
  { name: "News", link: "/news" },
  { name: "FAQs", link: "/faq" },
  { name: "Contacts", link: "/contactus" },
];

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
  };

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate('/home');
  };

  return (
    <div
      className={`position-sticky top-0 ${
        windowWidth < 992 ? "bg-white border-bottom cus_header" : "blurred-header"
      }`}
      style={{ zIndex: 1030 }}
    >
      {/* Mobile Navigation */}
      <Navbar expand="lg" className="d-lg-none bg- shadow-sm">
        <Container>
          <Navbar.Brand as={Link} to="/home">
            <img
              src={Logo}
              alt="logo"
              className={`logo_img ${isScrolled ? "small-logo" : ""}`}
            />
          </Navbar.Brand>
          <div className="ms-auto d-flex align-items-center">
            {!isAuthenticated && (
              <button
                onClick={() => setShowLoginModal(true)}
                className="btn login-btn text-white position-relative overflow-hidden me-2"
                style={{ fontSize: "18px", fontFamily: "inherit" }}
              >
                <div className="swift-btn_animation d-flex justify-content-center align-items-center log_cus">
                  <p className="m-0 text-black">Login</p>
                </div>
              </button>
            )}
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
          </div>

          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              {navItems.map((item, index) => (
                <Nav.Link
                  as={Link}
                  key={index}
                  to={item.link}
                  className={location.pathname === item.link ? "text-warning" : "text-dark"}
                >
                  {item.name}
                </Nav.Link>
              ))}
            </Nav>

            {!isAuthenticated && (
              <button
                onClick={() => setShowSignUpModal(true)}
                className="btn signup-btn text-white position-relative overflow-hidden border-0 mb-3"
                style={{
                  fontSize: "18px",
                  fontFamily: "inherit",
                  background: "radial-gradient(circle,rgb(255, 240, 200),rgb(255, 255, 255))"
                }}
              > 
                <div className="swift-btn_animation d-flex justify-content-center align-items-center"
                  style={{
                    background: "radial-gradient(circle,rgb(0, 0, 0),rgb(0, 0, 0))",
                  }}
                >
                  <FaUserPlus className="me-2 text-black" />
                  <p className="m-0 text-black fw-bold button_name">Sign Up</p>
                </div>
              </button>
            )}
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Desktop Navigation */}
      <header className="py-2 order-1 d-none d-lg-block main_head_bg">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <Link to="/home" className="d-flex align-items-center">
              <img 
                src={Logo} 
                alt="logo" 
                className={`logo_img ${isScrolled ? "small-logo" : ""}`}
              />
            </Link>

            <nav className="d-flex align-items-center">
              <ul className="nav">
                {navItems.map((item, index) => (
                  <li key={index} className="nav-item">
                    <Link
                      to={item.link}
                      className={`nav-link nav_buts px-3 mx-2 ${
                        location.pathname === item.link ? "active-dot" : "text-black"
                      }`}
                      style={{ fontSize: "18px", fontFamily: "inherit" }}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="ms-4" ref={dropdownRef}>
                {isAuthenticated ? (
                  <div className="position-relative">
                    <button
                      onClick={() => setOpen(!open)}
                      className="d-flex align-items-center bg-transparent border-0"
                    >
                      <FaUserCircle className="text-dark" size={24} />
                      <span className="ms-2 d-none d-md-inline">
                        {user?.email?.split('@')[0] || 'Profile'}
                      </span>
                    </button>

                    {open && (
                      <div 
                        className="dropdown-menu show p-3"
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          minWidth: '180px',
                          zIndex: 1000,
                        }}
                      >
                        <div className="d-flex flex-column gap-2">
                          <Link 
                            to="/userprofile" 
                            className="d-flex align-items-center gap-2 text-decoration-none text-secondary"
                            onClick={() => setOpen(false)}
                          >
                            <FaUser size={14} /> My Profile
                          </Link>
                          <button 
                            className="d-flex align-items-center gap-2 bg-transparent border-0 p-0 text-secondary"
                            onClick={handleLogout}
                          >
                            <FaSignOutAlt size={14} /> Logout
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="d-flex">
                    <button
                      onClick={() => setShowLoginModal(true)}
                      className="btn signup-btn text-black position-relative overflow-hidden border-0 me-2"
                    >
                      <div
                        className="swift-btn_animation d-flex justify-content-center align-items-center"
                        style={{
                          background: "radial-gradient(circle,rgb(255, 240, 200),rgb(255, 255, 255))",
                        }}
                      >
                        <p className="m-0 fw-bold text-black">Login</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setShowSignUpModal(true)}
                      className="btn signup-btn text-white position-relative overflow-hidden border-0"
                    >
                      <div
                        className="swift-btn_animation d-flex justify-content-center align-items-center"
                        style={{
                          background: "radial-gradient(circle,rgb(0, 0, 0),rgb(0, 0, 0))",
                        }}
                      >
                        <p className="m-0 text-black fw-bold button_name">Sign Up</p>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Modals */}
      <Login 
        show={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
        onLoginSuccess={handleLoginSuccess} 
      />
      <SignUpModal 
        show={showSignUpModal} 
        onClose={() => setShowSignUpModal(false)}
        onSignUpSuccess={() => {
          setShowSignUpModal(false);
          setShowLoginModal(true);
        }}
      />
    </div>
  );
}

export default Header;